import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { IServicer, IPreference, IOrganization } from '@/Interfaces';
import { IServicerGroup } from './IServicerGroup';
import { AjaxHelper, AjaxHelperException, IPaginatedList } from '@samc/common';

import { Field, DropdownField, Action, Validations } from '@samc/centricity-core';
import { ServicerSource } from '@/Sources';

import { ServicerFormModal } from '@/Components/Modals';
import Entitlements from '@/Entitlements';
import { isNullOrEmpty } from '../../utils';

@Component({
  name: 'servicer-selector',
  components: { Field, DropdownField, Action }
})
export default class ServicerSelectorComponent extends Vue {
  private dataSourceUrl = '/datasource';
  @Prop({ type: Boolean, default: false }) public addServicer!: boolean;

  @Prop({ required: true }) public verificationToken!: string;
  @Prop() public value!: IServicerGroup;
  @Prop() public fieldClass!: string | string[] | Record<string, boolean>;
  @Prop({ default: () => null }) public validations!: Validations<IServicerGroup> | null;
  @Prop({ type: Boolean, default: false }) public clearable!: boolean;
  @Prop({ type: Boolean, default: false }) public loadPreferences!: boolean;
  @Prop({ type: Boolean, default: false }) public disabled!: boolean;

  public get servicerLoading(): boolean { return !!this.model.servicerId && !this.servicerSource.isLoaded; }

  public servicerId: string | null = null;
  public helper: AjaxHelper | null = null;
  public model: IServicerGroup = {
    servicerId: ""
  };

  public servicerSource = new ServicerSource(true);
  public entitledToEditCounterparty: boolean = false;

  public async mounted() {
    this.helper = new AjaxHelper(this.dataSourceUrl, this.verificationToken, null, null);

    if (this.model.servicerId === null && this.loadPreferences) {
      this.model.servicerId = await this.getDefaultServicer();
    }
  }

  //#region Add New Items
  public async addNewServicer() {
    let newOrganization = await this.$modal<IOrganization | undefined>({ component: ServicerFormModal, title: 'Add New Servicer', width: 450 });
    if (this.helper !== null && name !== undefined && this.$user !== null && newOrganization !== undefined) {
      try {
        this.$loading('Creating Servicer');
        newOrganization.createdBy = this.$user?.email;
        const servicer = await this.helper.postData<IServicer>('servicers', newOrganization);
        if (!Array.isArray(this.servicerSource)) {
          this.servicerSource.clientItems.push(servicer);
        }
        this.servicerSource = new ServicerSource(true);
        this.model.servicerId = servicer.id;
      }catch (e: any) {
        this.$log('Failed Adding New Servicer', e);
        if (e instanceof AjaxHelperException) {
          // TODO: This needs to be updated when we change error logging changes
          this.$alert.error(`Servicer name: ${e.data ?? e.message}`);
        } else {
          this.$alert.error(`An error occurred`);
        }
      } finally {
        this.$loading();
      }
    }
  }
  //#endregion

  //#region Default Preferences
  public async getDefaultServicer() {
    const key = 'DefaultServicer';
    const defServicer = await this.helper?.getData<IPreference>('userpreference', { key });
    if (!defServicer) {
      return '';
    }
    return defServicer.value.toLowerCase();
  }
  
  /* 
   This will be moved to backend
  */
  public getBestMatch(prefs: IPreference[], key: string, scope0?: string, scope1?: string, scope2?: string) {
    let bmIdx = 0;
    let bmKeys = -1;
    for (let i = 0; i < prefs.length; i++) {
      let keyMatch = 0;
      if (prefs[i].key.toLowerCase() === key.toLowerCase()) { keyMatch++; }
      if (scope0 && prefs[i].scope0.toLowerCase() === scope0.toLowerCase()) { keyMatch++; }
      if (scope1 && prefs[i].scope1.toLowerCase() === scope1.toLowerCase()) { keyMatch++; }
      if (scope2 && prefs[i].scope2.toLowerCase() === scope2.toLowerCase()) { keyMatch++; }
      if (keyMatch > bmKeys) {
        bmKeys = keyMatch;
        bmIdx = i;
      }
    }
    return prefs[bmIdx];
  }
  //#endregion

  //#region Watchers
  @Watch('model.servicerId', { deep: true })
  public async onServicerChange(newServicerId: string | null) {
    this.$log('Update servicerId', newServicerId);

    if (this.servicerSource.items.length > 0) {
      const servicer = this.servicerSource.items.find(x => x.id === newServicerId) ?? null;
      this.$emit('servicer', servicer);
      this.$emit('input', this.model);
    }
  }

  @Watch('servicerSource.isLoaded')
  public onServicerSourceIsLoadedChange(newIsLoaded: boolean) {
    if (newIsLoaded && !isNullOrEmpty(this.model.servicerId)) {
      this.$log('Servicer Loaded After Default Servicer');
      const servicer = this.servicerSource.find(x => x.id === this.model.servicerId);
      if (servicer !== undefined)
        this.$emit('servicer', servicer);
        this.$emit('input', this.model);
    }
  }

  @Watch('value', { immediate: true })
  public onValueChange(newValue?: IServicerGroup) {
    if (newValue !== undefined) {
      this.model = newValue;
    }
  }

  @Watch('verificationToken')
  public onVerificationTokenChange(newVerificationToken: string) {
    this.helper = new AjaxHelper(this.dataSourceUrl, newVerificationToken, null, null);
  }

  @Watch('$user', { immediate: true })
  public setEditableOptions() {
    if (!this.$user) {
      this.entitledToEditCounterparty = false;
      return;
    }
    this.entitledToEditCounterparty = this.$user.hasEntitlement(Entitlements.VendorsContactsModule.EdiCounterparties);
  }
  //#endregion
}
